
import { Component, Vue, Watch, Prop, Mixins } from "vue-property-decorator";
import { Getter, Action, Mutation, namespace } from "vuex-class";

import getScript from "@/services/insertScript.service";
import GoogleMap from "@/scripts/mapping/map";

import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { DispatchRequest, MobileFeatures, SVLocation, DispatchType, ILayerMap } from "@/store/mobile/types";
import { Guard, PatrolZone } from "@/store/dispatch/types";
import { dispatch } from "rxjs/internal/observable/pairs";
import { axiosInstance } from "@/axios.instance";
import { MapBounds, MobileMarker } from "@/store/mobile/types";
import { AxiosResponse } from "axios";

import DispatchInfoWindow from "@/components/mobile/dispatch/DispatchInfoWindow.vue";
import MobileMap from "@/components/mobile/core/MobileMap.vue";

import DispatchMixin from "@/mixins/DispatchMixin";
import { MapLayerData } from "@/store/map-layers/types";

import { stringToBounds, getEnhancedMapping, IEnhancedMapping, ILayer } from "@sureview/v2-mapping";
import { mobile } from '@/store/mobile';

class LocationBounds {
	location: google.maps.LatLng;
	viewport: {
		northeast: google.maps.LatLng;
		southwest: google.maps.LatLng;
	};
}

const Mobile = namespace("mobile");
const MapLayers = namespace("mapLayers");

declare global {
	interface Window {
		infoWindow: any;
		closeInfoWindow: any;
		claimUnclaimHandler: any;
		activateInactivateHandler: any;
		markAsArrived: any;
		markerClickHandler: any;
	}
}

@Component({
	components: {
		"mobile-map": MobileMap
	}
})
export default class DispatchMap extends Mixins(DispatchMixin) {
	@Mobile.Getter("getClaimedDispatchRequests") claimedDispatchRequests: DispatchRequest[];
	@Mobile.Getter("getActiveDispatchRequest") activeDispatchRequest: DispatchRequest;

	@Mobile.Action loadNotesForEvent: any;
	@Mobile.Mutation setAlmostArrived: (almostArrived: boolean) => void;

    @Prop({ default: () => this.defaultMapZoomLevel })
	public zoomLevel;

	@Prop({ default: () => false })
	public prototype: boolean;

	@Prop({ default: () => null })
	public guardLocationForManualUpdate: any;

	private mapMarkersToDisplay: MobileMarker[] = [];
	private closeInfoWindow: boolean = false;
	private showDirectionsToLocation: google.maps.LatLng = null;
	private hideDirections: boolean = false;

	// @techdebt - this function is not used in this version, but will be in a future phase
	@Watch("claimedDispatchRequests")
	onClaimedRequestsChanged(value: DispatchRequest[], oldValue: DispatchRequest[]) {
		if (!this.activeDispatchRequest) {
			// When we're not showing the active dispatch, show all of the claimed dispatches
			const dispatchRequests = this.displayDispatchRequest(value);
			this.mapMarkersToDisplay = this.mapDispatchRequestsToMobileMarkers(dispatchRequests);
		}		
	}

	@Watch("activeDispatchRequest")
	onActiveDispatchRequest(value: DispatchRequest, oldValue: DispatchRequest) {
		if (value && oldValue) {
			if (value.dispatchQueueID !== oldValue.dispatchQueueID) {
				this.closeInfoWindow = !this.closeInfoWindow;
			}

			// If this is a new active dispatch, show only this on the map
			this.mapMarkersToDisplay = this.mapDispatchRequestsToMobileMarkers([value]);
		}

		if (value) {
			this.showDirectionsToLocation = this.parseLocationToLatLng(value.location);

			// Pre-load the notes for an event
			this.loadNotesForEvent(this.activeDispatchRequest.eventID);
		} else {
			this.setAlmostArrived(false);
			this.hideDirections = !this.hideDirections;
		}
	}

	private mapDispatchRequestsToMobileMarkers(dispatchRequests: DispatchRequest[]): MobileMarker[] {
		Vue.extend(DispatchInfoWindow);
		const dispatchInfoWindow = new DispatchInfoWindow({
			store: this.$store
		}).$mount();

		let mobileMarkers = dispatchRequests.map(x => {
			let mobileMarker: MobileMarker = {
				id : x.dispatchQueueID,
				title : x.areaTitle + ": " + (x.serverTypeEventTitle ? x.serverTypeEventTitle : ""),
				location : this.parseLocationToLatLng(x.location),
				iconURL : this.getMarkerIconUrl(x.claimed, x.dispatchType),
				color: this.getMarkerCircleColor(x.dispatchType),
				infoWindow: dispatchInfoWindow,
				googleMarker: null,
				object: null
			}
			return mobileMarker;
		})

		return mobileMarkers;
	}

	private getMarkerIconUrl(claimed: boolean, dispatchType: string = "Dispatch"): string {		
		let urlIcon = "images/enhancedmapping/bell-solid.svg";

		if (this.isTourType(dispatchType)) {
			urlIcon = "images/enhancedmapping/tour.svg";
			if (this.isAssessmentType(dispatchType)) {
				urlIcon = 'images/enhancedmapping/assessment.svg'
			}
		} else if (!dispatchType || DispatchType.Dispatch == dispatchType) {
			// Make a blue icon for claimed, a red one for unclaimed
			urlIcon = claimed ? "images/enhancedmapping/bell-claimed.svg" : "images/enhancedmapping/bell-solid.svg";
		}

		return urlIcon;
	}
}
